import Logo from './Logo';
import Footer from './Footer';
import { PRIVACY_EMAIL } from '../constants';
import './PrivacyPolicy.css';

const LAST_UPDATED = 'July 21, 2026';

export default function PrivacyPolicy() {
  return (
    <div className="app">
      <header className="legal-nav">
        <div className="container legal-nav__inner">
          <a href="#top" className="nav__brand" aria-label="Peek home">
            <Logo />
          </a>
          <a href="#top" className="legal-nav__back">
            &larr; Back to home
          </a>
        </div>
      </header>

      <main className="legal">
        <div className="container legal__inner">
          <p className="eyebrow">Legal</p>
          <h1 className="legal__title">Privacy Policy</h1>
          <p className="legal__updated">Last updated: {LAST_UPDATED}</p>

          <div className="legal__body">
            <p>
              Peek (&ldquo;Peek,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or
              &ldquo;our&rdquo;) helps you discover movies and TV shows through
              recommendations from friends, family, and Peekster, our AI
              assistant. This Privacy Policy explains what information we collect,
              how we use it, and the choices you have. By using the Peek app, you
              agree to the practices described here.
            </p>

            <section>
              <h2>Information we collect</h2>
              <ul>
                <li>
                  <strong>Account information.</strong> When you create an
                  account, we collect details such as your name, username, email
                  address, and profile photo.
                </li>
                <li>
                  <strong>Content you share.</strong> Recommendations, likes,
                  comments, lists, and video reactions you create, as well as the
                  movies and shows you interact with.
                </li>
                <li>
                  <strong>Social connections.</strong> The friends and family you
                  connect with and follow within Peek.
                </li>
                <li>
                  <strong>Peekster conversations.</strong> The messages and
                  prompts you send to Peekster so it can understand your mood and
                  preferences and suggest something to watch.
                </li>
                <li>
                  <strong>Usage and device information.</strong> How you use the
                  app, along with device type, operating system, app version, and
                  general diagnostic data.
                </li>
              </ul>
            </section>

            <section>
              <h2>How we use your information</h2>
              <ul>
                <li>Provide, personalize, and improve the Peek experience.</li>
                <li>
                  Generate recommendations from your friends, your activity, and
                  Peekster.
                </li>
                <li>
                  Enable social features such as sharing, comments, and video
                  reactions.
                </li>
                <li>Keep Peek safe, secure, and free of abuse.</li>
                <li>
                  Communicate with you about updates, features, and support.
                </li>
              </ul>
            </section>

            <section>
              <h2>Peekster and AI</h2>
              <p>
                When you chat with Peekster, we process your messages to
                understand what you&apos;re in the mood for and to recommend
                movies and shows. We use these conversations to provide and
                improve Peekster&apos;s recommendations. Please avoid sharing
                sensitive personal information in your Peekster chats.
              </p>
            </section>

            <section>
              <h2>How we share information</h2>
              <p>We do not sell your personal information. We share it only:</p>
              <ul>
                <li>
                  <strong>With other users,</strong> according to your activity
                  and settings — for example, recommendations and reactions you
                  choose to share are visible to the people you share them with.
                </li>
                <li>
                  <strong>With service providers</strong> who help us operate the
                  app (such as hosting, analytics, and AI processing), under
                  agreements that protect your information.
                </li>
                <li>
                  <strong>For legal reasons,</strong> when required by law or to
                  protect the rights, safety, and security of Peek and its users.
                </li>
              </ul>
            </section>

            <section>
              <h2>Your choices and rights</h2>
              <ul>
                <li>Access, update, or correct your account information.</li>
                <li>
                  Delete your account and associated content at any time from
                  within the app.
                </li>
                <li>
                  Manage notifications and privacy settings that control what you
                  share.
                </li>
                <li>
                  Depending on where you live, you may have additional rights to
                  access, port, or delete your data. Contact us to exercise them.
                </li>
              </ul>
            </section>

            <section>
              <h2>Data retention</h2>
              <p>
                We keep your information for as long as your account is active or
                as needed to provide Peek. When you delete your account, we delete
                or de-identify your personal information, except where we must
                retain it to comply with legal obligations or resolve disputes.
              </p>
            </section>

            <section>
              <h2>Security</h2>
              <p>
                We use reasonable technical and organizational measures to protect
                your information. No method of transmission or storage is fully
                secure, so we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2>Children&apos;s privacy</h2>
              <p>
                Peek is not intended for children under 13 (or the minimum age
                required in your country). We do not knowingly collect personal
                information from children. If you believe a child has provided us
                information, please contact us and we will remove it.
              </p>
            </section>

            <section>
              <h2>Changes to this policy</h2>
              <p>
                We may update this Privacy Policy from time to time. When we do,
                we will revise the &ldquo;Last updated&rdquo; date above and, where
                appropriate, notify you within the app.
              </p>
            </section>

            <section>
              <h2>Contact us</h2>
              <p>
                If you have questions about this Privacy Policy or your
                information, reach us at{' '}
                <a className="legal__link" href={`mailto:${PRIVACY_EMAIL}`}>
                  {PRIVACY_EMAIL}
                </a>
                .
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
